/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Import Workspace
 *
 * File:
 * src/modules/campaign/prospectImport/components/importWorkspace/validation/ValidationDashboard.jsx
 *
 * Description:
 * Validation Dashboard for ERP Import Framework.
 *
 * Responsibilities
 * ----------------
 * • Display Validation Summary
 * • Display KPI Cards
 * • Display Validation Progress
 * • Display Validation Checklist
 * • Display Ready For Import Status
 *
 * No business logic.
 * No API calls.
 * ============================================================================
 */

import React from "react";

import {
    Card,
    Row,
    Col,
    ProgressBar,
    Alert
} from "react-bootstrap";

export default function ValidationDashboard({

    config,

    state

}) {

    // ==========================================================
    // STATE
    // ==========================================================

    const {

        summary = {},

        verified

    } = state;

    const {

        total_rows = 0,

        valid_rows = 0,

        duplicate_rows = 0,

        warning_rows = 0,

        invalid_rows = 0

    } = summary;

    // ==========================================================
    // KPI CARD
    // ==========================================================

    const renderKpiCard = (

        title,

        value,

        bgClass,

        icon

    ) => (

        <Col lg={2} md={4} sm={6} className="mb-3">

            <Card className={`border-${bgClass} h-100`}>

                <Card.Body className="text-center">

                    <div className="mb-2">

                        <i
                            className={`${icon} text-${bgClass}`}
                            style={{ fontSize: "1.75rem" }}
                        ></i>

                    </div>

                    <h4 className="mb-1">

                        {value}

                    </h4>

                    <small className="text-muted">

                        {title}

                    </small>

                </Card.Body>

            </Card>

        </Col>

    );

    return (

        <Card className="shadow-sm mt-4">

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <Card.Header>

                <h5 className="mb-0">

                    Validation Summary

                </h5>

            </Card.Header>

            <Card.Body>

                {/* ================================================== */}
                {/* KPI CARDS */}
                {/* ================================================== */}

                <Row>

                    {

                        renderKpiCard(

                            "Total Rows",

                            total_rows,

                            "primary",

                            "fas fa-table"

                        )

                    }

                    {

                        renderKpiCard(

                            "Valid",

                            valid_rows,

                            "success",

                            "fas fa-check-circle"

                        )

                    }

                    {

                        renderKpiCard(

                            "Duplicate",

                            duplicate_rows,

                            "warning",

                            "fas fa-copy"

                        )

                    }

                    {

                        renderKpiCard(

                            "Warnings",

                            warning_rows,

                            "info",

                            "fas fa-exclamation-circle"

                        )

                    }

                    {

                        renderKpiCard(

                            "Invalid",

                            invalid_rows,

                            "danger",

                            "fas fa-times-circle"

                        )

                    }

                </Row>
                                {/* ================================================== */}
                {/* VALIDATION PROGRESS */}
                {/* ================================================== */}

                <Card className="border-0 bg-light mt-3">

                    <Card.Body>

                        <div className="d-flex justify-content-between mb-2">

                            <strong>

                                Validation Progress

                            </strong>

                            <strong className="text-success">

                                {verified ? "100%" : "0%"}

                            </strong>

                        </div>

                        <ProgressBar

                            now={verified ? 100 : 0}

                            variant="success"

                            animated={!verified}

                            striped={!verified}

                        />

                    </Card.Body>

                </Card>

                {/* ================================================== */}
                {/* VALIDATION CHECKLIST */}
                {/* ================================================== */}

                <Row className="mt-4">

                    <Col lg={6}>

                        <Card className="h-100">

                            <Card.Header>

                                <strong>

                                    Validation Checklist

                                </strong>

                            </Card.Header>

                            <Card.Body>

                                <div className="mb-3">

                                    <i className="fas fa-check-circle text-success me-2"></i>

                                    File Uploaded Successfully

                                </div>

                                <div className="mb-3">

                                    <i className="fas fa-check-circle text-success me-2"></i>

                                    Template Structure Verified

                                </div>

                                <div className="mb-3">

                                    <i className="fas fa-check-circle text-success me-2"></i>

                                    Required Columns Verified

                                </div>

                                <div className="mb-3">

                                    <i className="fas fa-check-circle text-success me-2"></i>

                                    Duplicate Validation Completed

                                </div>

                                <div>

                                    <i className="fas fa-check-circle text-success me-2"></i>

                                    Business Validation Completed

                                </div>

                            </Card.Body>

                        </Card>

                    </Col>

                    {/* ================================================== */}
                    {/* IMPORT STATUS */}
                    {/* ================================================== */}

                    <Col lg={6}>

                        <Card
                            className="h-100 border-success"
                        >

                            <Card.Header
                                className="bg-success text-white"
                            >

                                Ready for Import

                            </Card.Header>

                            <Card.Body
                                className="d-flex flex-column justify-content-center"
                            >

                                <h5 className="text-success">

                                    <i className="fas fa-check-circle me-2"></i>

                                    Validation Completed

                                </h5>

                                <p className="mb-0">

                                    The uploaded file has passed all
                                    validation checks.

                                </p>

                                <p className="mb-0">

                                    You may now review the imported
                                    records before performing the
                                    final import.

                                </p>

                            </Card.Body>

                        </Card>

                    </Col>

                </Row>
                                {/* ================================================== */}
                {/* VALIDATION MESSAGE */}
                {/* ================================================== */}

                <Alert

                    variant={
                        verified
                            ? "success"
                            : "warning"
                    }

                    className="mt-4 mb-0"

                >

                    {

                        verified

                            ? (

                                <>

                                    <i className="fas fa-check-circle me-2"></i>

                                    File verification completed successfully.

                                    Review the records below before importing.

                                </>

                            )

                            : (

                                <>

                                    <i className="fas fa-exclamation-triangle me-2"></i>

                                    File verification has not been completed.

                                </>

                            )

                    }

                </Alert>

            </Card.Body>

        </Card>

    );

}